import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  Container,
  Zoom,
  Snackbar,
  Alert,
  styled,
  Backdrop,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  Token,
  Refresh,
  TrendingUp,
  Assessment,
  RestartAlt,
  CheckCircle,
  Warning,
  ClearAll,
  Sync, // Add your desired icon here
} from "@mui/icons-material";
import axios from "axios";
import { apiUrl } from "../../constants";

const StyledContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  paddingTop: "2rem",
  paddingBottom: "2rem",
  width: "100%",
}));

const TokenCard = styled(Card)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
  },
}));

const ResetButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)",
  color: "white",
  borderRadius: "6px",
  padding: "6px 12px",
  textTransform: "none",
  fontSize: "0.8rem",
  fontWeight: "600",
  minWidth: "90px",
  height: "32px",
  "&:hover": {
    background: "linear-gradient(135deg, #e11d48 0%, #be123c 100%)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(244, 63, 94, 0.3)",
  },
  "&:disabled": {
    background: "#e5e7eb",
    color: "#9ca3af",
    transform: "none",
    boxShadow: "none",
  },
  transition: "all 0.2s ease",
}));

const ResetAllButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
  color: "white",
  borderRadius: "8px",
  padding: "10px 20px",
  textTransform: "none",
  fontSize: "0.9rem",
  fontWeight: "600",
  minWidth: "140px",
  "&:hover": {
    background: "linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)",
    transform: "translateY(-1px)",
    boxShadow: "0 6px 16px rgba(220, 38, 38, 0.3)",
  },
  "&:disabled": {
    background: "rgba(255, 255, 255, 0.3)",
    color: "rgba(255, 255, 255, 0.6)",
    transform: "none",
    boxShadow: "none",
  },
  transition: "all 0.2s ease",
}));

const NewActionButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
  color: "white",
  borderRadius: "8px",
  padding: "10px 20px",
  textTransform: "none",
  fontSize: "0.9rem",
  fontWeight: "600",
  minWidth: "140px",
  "&:hover": {
    background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
    transform: "translateY(-1px)",
    boxShadow: "0 6px 16px rgba(59, 130, 246, 0.3)",
  },
  "&:disabled": {
    background: "rgba(255, 255, 255, 0.3)",
    color: "rgba(255, 255, 255, 0.6)",
    transform: "none",
    boxShadow: "none",
  },
  transition: "all 0.2s ease",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: "white",
  fontWeight: "700",
  marginBottom: "2rem",
  textAlign: "center",
  textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "8px",
  padding: "6px 8px",
  background: "rgba(103, 126, 234, 0.04)",
  borderRadius: "6px",
  border: "1px solid rgba(103, 126, 234, 0.08)",
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontWeight: "500",
  color: "#4b5563",
  fontSize: "0.8rem",
  display: "flex",
  alignItems: "center",
  gap: "6px",
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  color: "#1f2937",
  fontSize: "0.9rem",
  fontWeight: "700",
  display: "flex",
  alignItems: "center",
  gap: "4px",
}));

const StatsChip = styled(Chip)(({ theme }) => ({
  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
  color: "white",
  fontWeight: "600",
  fontSize: "0.75rem",
  height: "28px",
  "& .MuiChip-label": {
    padding: "0 12px",
  },
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "6px",
  backgroundColor: "#e5e7eb",
  borderRadius: "3px",
  overflow: "hidden",
  position: "relative",
}));

const ProgressFill = styled(Box)(({ percentage }) => ({
  height: "100%",
  background:
    percentage === 100
      ? "linear-gradient(90deg, #10b981 0%, #059669 100%)"
      : "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
  width: `${Math.min(Math.max(percentage, 0), 100)}%`,
  borderRadius: "3px",
  transition: "width 0.3s ease",
}));

const TokenManagement = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [resetDialog, setResetDialog] = useState({
    open: false,
    tokenId: null,
    tokenData: null,
  });
  const [resetAllDialog, setResetAllDialog] = useState({ open: false });
  const [newActionDialog, setNewActionDialog] = useState({ open: false }); // New state
  const [tokens, setTokens] = useState([]);

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const resTokens = await axios.get(`${apiUrl}/activitytoken/names`);
      if (resTokens.data.success) {
        setTokens(resTokens.data.data);
      }
    } catch (error) {
      console.error("Error fetching tokens:", error);
      setToast({
        open: true,
        message: "Failed to fetch tokens",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetToken = async (tokenId) => {
    setLoading(true);
    try {
      const resParticularRest = await axios.put(
        `${apiUrl}/activitytoken//reset-particular`,
        {
          activityId: tokenId,
        }
      );
      setToast({
        open: true,
        message: "Token reset successfully!",
        severity: "success",
      });
      setResetDialog({ open: false, tokenId: null, tokenData: null });
      await fetchTokens();
    } catch (error) {
      setToast({
        open: true,
        message: "Failed to reset token",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handleResetAllTokens = async () => {
    setLoading(true);
    try {
      const resResetAllTokens = await axios.post(
        `${apiUrl}/activitytoken/reset-all`
      );
      setToast({
        open: true,
        message: "All tokens reset successfully!",
        severity: "success",
      });
      setResetAllDialog({ open: false });
      await fetchTokens();
    } catch (error) {
      setToast({
        open: true,
        message: "Failed to reset all tokens",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handleNewAction = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${apiUrl}/admin/manual-reset`); 
      
      setToast({
        open: true,
        message: "Action completed successfully!",
        severity: "success",
      });
      setNewActionDialog({ open: false });
      
      await fetchTokens();
    } catch (error) {
      setToast({
        open: true,
        message: "Failed to complete action",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const openResetDialog = (token) => {
    setResetDialog({ open: true, tokenId: token.activityId, tokenData: token });
  };

  const closeResetDialog = () => {
    setResetDialog({ open: false, tokenId: null, tokenData: null });
  };

  const openResetAllDialog = () => {
    setResetAllDialog({ open: true });
  };

  const closeResetAllDialog = () => {
    setResetAllDialog({ open: false });
  };

  // New dialog handlers
  const openNewActionDialog = () => {
    setNewActionDialog({ open: true });
  };

  const closeNewActionDialog = () => {
    setNewActionDialog({ open: false });
  };

  const calculateProgress = (count, overAllCount) => {
    if (overAllCount === 0) return 0;
    const progress = (count / overAllCount) * 100;
    return Math.min(Math.max(progress, 0), 100);
  };

  const getProgressStatus = (count, overAllCount) => {
    const progress = calculateProgress(count, overAllCount);
    if (progress >= 100)
      return { icon: CheckCircle, color: "#10b981", text: "Completed" };
    if (progress >= 50)
      return { icon: TrendingUp, color: "#f59e0b", text: "In Progress" };
    return { icon: Warning, color: "#ef4444", text: "Just Started" };
  };

  const hasActiveTokens =
    tokens && tokens.length > 0 && tokens.some((token) => token.count > 0);

  useEffect(() => {
    fetchTokens();
  }, []);

  if (!tokens || tokens.length === 0) {
    return (
      <StyledContainer>
        <Container maxWidth="lg">
          <SectionTitle variant="h4">
            <Token sx={{ mr: 1, verticalAlign: "middle" }} />
            Token Management
          </SectionTitle>
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Token
              sx={{
                fontSize: "4rem",
                color: "rgba(255, 255, 255, 0.7)",
                mb: 2,
              }}
            />
            <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
              No Tokens Found
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              There are currently no tokens to display.
            </Typography>
          </Box>
        </Container>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <Container maxWidth="lg">
        <Backdrop
          open={loading}
          sx={{
            zIndex: 1010,
            backdropFilter: "blur(4px)",
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: "#667eea" }} />
        </Backdrop>

        <SectionTitle variant="h4">
          <Token sx={{ mr: 1, verticalAlign: "middle" }} />
          Token Management
        </SectionTitle>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <StatsChip
            label={`${tokens.length} Token${
              tokens.length !== 1 ? "s" : ""
            } Active`}
            icon={<Token sx={{ fontSize: "1rem !important" }} />}
          />
          
          {/* New Action Button */}
          <NewActionButton
            onClick={openNewActionDialog}
            startIcon={<Sync />}
          >
            Delete Token
          </NewActionButton>
          
          <ResetAllButton
            onClick={openResetAllDialog}
            startIcon={<ClearAll />}
            disabled={!hasActiveTokens}
          >
            Reset All
          </ResetAllButton>
        </Box>

        <Grid container spacing={2}>
          {tokens.map((token, index) => {
            const progress = calculateProgress(token.count, token.overAllCount);
            const status = getProgressStatus(token.count, token.overAllCount);
            const StatusIcon = status.icon;

            return (
              <Grid item xs={12} md={6} lg={4} key={token.activityId}>
                <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                  <TokenCard>
                    <CardContent sx={{ p: 2 }}>
                      {/* Header */}
                      <Box sx={{ mb: 2, textAlign: "center" }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "700",
                            color: "#1f2937",
                            fontSize: "1rem",
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {token.activityName || "Activity"}
                        </Typography>
                      </Box>

                      {/* Progress Section */}
                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "600",
                              color: "#374151",
                              fontSize: "0.8rem",
                            }}
                          >
                            Progress
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <StatusIcon
                              sx={{ fontSize: "0.9rem", color: status.color }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: "600",
                                color: status.color,
                                fontSize: "0.75rem",
                              }}
                            >
                              {Math.round(progress)}%
                            </Typography>
                          </Box>
                        </Box>
                        <ProgressBar>
                          <ProgressFill percentage={progress} />
                        </ProgressBar>
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: "center",
                            mt: 0.5,
                            color: "#6b7280",
                            fontSize: "0.75rem",
                          }}
                        >
                          {token.count} / {token.overAllCount} completed
                        </Typography>
                      </Box>

                      {/* Token Details */}
                      <Box sx={{ mb: 2 }}>
                        <InfoRow>
                          <InfoLabel>
                            <Assessment
                              sx={{ fontSize: "0.9rem", color: "#667eea" }}
                            />
                            Current Token Count:
                          </InfoLabel>
                          <InfoValue sx={{ color: "#667eea" }}>
                            {token.count}
                          </InfoValue>
                        </InfoRow>

                        <InfoRow>
                          <InfoLabel>
                            <Token
                              sx={{ fontSize: "0.9rem", color: "#667eea" }}
                            />
                            Total Token Given:
                          </InfoLabel>
                          <InfoValue sx={{ color: "#10b981" }}>
                            {token.overAllCount}
                          </InfoValue>
                        </InfoRow>
                      </Box>

                      {/* Action Section */}
                      <Box
                        sx={{
                          background: "rgba(244, 63, 94, 0.04)",
                          borderRadius: "6px",
                          padding: "8px",
                          border: "1px solid rgba(244, 63, 94, 0.08)",
                          textAlign: "center",
                        }}
                      >
                        <ResetButton
                          onClick={() => openResetDialog(token)}
                          startIcon={
                            <RestartAlt
                              sx={{ fontSize: "0.9rem !important" }}
                            />
                          }
                          disabled={token.count === 0}
                        >
                          Reset
                        </ResetButton>
                      </Box>
                    </CardContent>
                  </TokenCard>
                </Zoom>
              </Grid>
            );
          })}
        </Grid>

        {/* Reset Confirmation Dialog */}
        <Dialog
          open={resetDialog.open}
          onClose={closeResetDialog}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: "700",
              color: "#1f2937",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Warning sx={{ color: "#f59e0b" }} />
            Confirm Token Reset
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: "#374151", mb: 2 }}>
              Are you sure you want to reset the tokens for this activity? This
              action will:
            </DialogContentText>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                • Reset activity token count to 0
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                • Reset Distributed Token to 0
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#ef4444", fontWeight: "600" }}
              >
                • This action cannot be undone
              </Typography>
            </Box>
            {resetDialog.tokenData && (
              <Box
                sx={{ mt: 2, p: 2, background: "#f9fafb", borderRadius: "8px" }}
              >
                <Typography variant="body2" sx={{ fontWeight: "600", mb: 1 }}>
                  Activity: {resetDialog.tokenData.activityName}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  Current Progress: {resetDialog.tokenData.count} /{" "}
                  {resetDialog.tokenData.overAllCount}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={closeResetDialog}
              sx={{
                color: "#6b7280",
                "&:hover": { background: "rgba(107, 114, 128, 0.1)" },
              }}
            >
              Cancel
            </Button>
            <ResetButton
              onClick={() => handleResetToken(resetDialog.tokenId)}
              startIcon={<RestartAlt />}
            >
              Reset Token
            </ResetButton>
          </DialogActions>
        </Dialog>

        {/* Reset All Confirmation Dialog */}
        <Dialog
          open={resetAllDialog.open}
          onClose={closeResetAllDialog}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: "700",
              color: "#1f2937",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ClearAll sx={{ color: "#dc2626" }} />
            Confirm Reset All Tokens
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: "#374151", mb: 2 }}>
              Are you sure you want to reset ALL tokens? This action will:
            </DialogContentText>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                • Reset all activity token counts to 0
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                • Reset Distributed Token to 0
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#dc2626", fontWeight: "600" }}
              >
                • This action cannot be undone
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 2,
                p: 2,
                background: "#fef2f2",
                borderRadius: "8px",
                border: "1px solid #fecaca",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#dc2626" }}
              >
                Warning: This will affect {tokens.length} token
                {tokens.length !== 1 ? "s" : ""}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                All user progress will be reset across all activities.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={closeResetAllDialog}
              sx={{
                color: "#6b7280",
                "&:hover": { background: "rgba(107, 114, 128, 0.1)" },
              }}
            >
              Cancel
            </Button>
            <ResetAllButton
              onClick={handleResetAllTokens}
              startIcon={<ClearAll />}
            >
              Reset All Tokens
            </ResetAllButton>
          </DialogActions>
        </Dialog>

        {/* New Action Confirmation Dialog */}
        <Dialog
          open={newActionDialog.open}
          onClose={closeNewActionDialog}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: "700",
              color: "#1f2937",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Sync sx={{ color: "#3b82f6" }} />
            Confirm Custom Action
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: "#374151", mb: 2 }}>
              Are you sure you want to perform this action? This will:
            </DialogContentText>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                • This will delete all tokens permanently.
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                • This action cannot be undone.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#f59e0b", fontWeight: "600" }}
              >
                • Please ensure you want to proceed
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={closeNewActionDialog}
              sx={{
                color: "#6b7280",
                "&:hover": { background: "rgba(107, 114, 128, 0.1)" },
              }}
            >
              Cancel
            </Button>
            <NewActionButton
              onClick={handleNewAction}
              startIcon={<Sync />}
            >
              Confirm Action
            </NewActionButton>
          </DialogActions>
        </Dialog>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            variant="filled"
            sx={{
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </StyledContainer>
  );
};

export default TokenManagement;